import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Button, Table, Tag, Tooltip, DatePicker, Typography, Space,
  Spin, Empty, message, Input, Divider, theme,
} from 'antd'
import {
  LeftOutlined, RightOutlined, ReloadOutlined,
  ArrowRightOutlined, WarningOutlined, CarOutlined, PrinterOutlined,
} from '@ant-design/icons'
import type { ColumnsType, ExpandableConfig } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import axiosInstance from '@/lib/axios'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import { usePageTitle } from '@/contexts/PageTitleContext'
import type { WorkOrder, Intervention, PurchaseRequest } from '@/types'
import CourtesyCarPlanningPanel from '@/components/CourtesyCarPlanningPanel'
import { AutoRefreshBadge } from '@/components/AutoRefreshBadge'
import { usePermission } from '@/hooks/usePermission'
import { printBoard } from '@/components/board/BoardPrintView'
import BoardGruppi from '@/components/board/BoardGruppi'
import type { InterventoBoard } from '@/components/board/BoardGruppi'
import type { BoardPrintRow } from '@/components/board/BoardPrintView'
import { TagArticoliResi } from '@/components/TagArticoliResi'

const { Text } = Typography
const { TextArea } = Input

interface DailyNote {
  id: number
  work_order_id: number
  data: string
  testo: string
  utente_id: number
  updated_at?: string
  wo_numero_scheda?: string
  wo_stato?: string
  vehicle_targa?: string
  vehicle_marca?: string
  vehicle_modello?: string
  vehicle_anno?: number
  customer_nome?: string
  customer_telefono?: string
}

interface BoardSnapshotItem {
  work_order_id: number
  numero_scheda: string
  stato_al_momento: string
  vehicle_targa?: string
  vehicle_marca?: string
  vehicle_modello?: string
  vehicle_anno?: number
  customer_nome?: string
  customer_telefono?: string
  priorita?: string
  data_appuntamento?: string
  data_fine_prevista?: string
  nota_id?: number
  nota_testo?: string
  nota_updated_at?: string
  workshop_nomi?: string[]
  data_entrata_veicolo?: string
}

interface WODetail {
  interventions: Intervention[]
  purchase_requests: PurchaseRequest[]
}

// Chi e' in garage lo decide il SERVER, in un posto solo
// (backend/app/services/presenza_officina.py): data di ingresso valorizzata e
// non successiva al giorno guardato, stato diverso da riconsegnata e annullata.
// Qui non si filtra piu' per stato: l'elenco scritto a mano che stava in questo
// punto era una seconda definizione dello stesso criterio, e divergeva da quella
// di "Lavori in officina". `in-officina` dice all'endpoint degli interventi di
// non restringere per stato: le auto le ha gia' scelte il criterio di presenza.
const ALIAS_STATI_IN_OFFICINA = 'in-officina'

const PRIORITA_COLORS: Record<string, string> = {
  urgente: 'red',
  alta: 'orange',
  media: 'gold',
  bassa: 'default',
}

const STATO_INT_COLORS: Record<string, string> = {
  'preso in carico': 'blue',
  'attesa componente': 'orange',
  sospeso: 'red',
  concluso: 'green',
}

const RDA_STATUS_MAP: Record<number, { nome: string; color: string }> = {
  1: { nome: 'Bozza', color: 'default' },
  2: { nome: 'In attesa', color: 'orange' },
  3: { nome: 'Approvata', color: 'green' },
  4: { nome: 'Rifiutata', color: 'red' },
  5: { nome: 'Ordinata', color: 'blue' },
  6: { nome: 'Ricevuta', color: 'cyan' },
  7: { nome: 'Annullata', color: 'default' },
}

function rdaStatusInfo(rda: PurchaseRequest) {
  return RDA_STATUS_MAP[rda.purchase_request_status_id] ?? {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nome: (rda as any).purchase_request_status_nome ?? `Stato ${rda.purchase_request_status_id}`,
    color: 'default',
  }
}

function InterventionTag({ int: it }: { int: Intervention }) {
  const nome = it.stato_intervento?.nome ?? 'senza stato'
  const color = STATO_INT_COLORS[nome.toLowerCase()] ?? 'default'
  return <Tag color={color} style={{ fontSize: '11px' }}>{nome}</Tag>
}

function WorkshopTags({ nomi }: { nomi?: string[] }) {
  if (!nomi?.length) return <Text type="secondary" style={{ fontSize: '11px' }}>—</Text>
  return (
    <Space size={2} wrap>
      {nomi.map(n => <Tag key={n} style={{ fontSize: '11px', margin: 0 }}>{n}</Tag>)}
    </Space>
  )
}

const INT_GRID = '20px 1fr 130px 100px 20px'

function WOExpandedRow({
  wo,
  selectedDate,
  noteText,
  onNoteChange,
  onNoteSave,
  savingNote,
  isPast,
}: {
  wo: WorkOrder
  selectedDate: string
  noteText: string
  onNoteChange: (text: string) => void
  onNoteSave: () => void
  savingNote: boolean
  isPast: boolean
}) {
  const { token } = theme.useToken()
  const [detail, setDetail] = useState<WODetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axiosInstance.get(`/work-orders/${wo.id}`).then(res => {
      setDetail({
        interventions: res.data.interventions ?? [],
        purchase_requests: res.data.purchase_requests ?? [],
      })
    }).catch(() => setDetail({ interventions: [], purchase_requests: [] }))
      .finally(() => setLoading(false))
  }, [wo.id])

  if (loading) return <div style={{ padding: '12px 24px' }}><Spin size="small" /></div>

  const ints = [...(detail?.interventions ?? [])].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
  const rdas = detail?.purchase_requests ?? []
  const conclusi = ints.filter(i => i.stato_intervento?.nome?.toLowerCase() === 'concluso').length

  // Raggruppa RDA per intervention_id (undefined = WO-level)
  const rdaByInt = new Map<number | undefined, PurchaseRequest[]>()
  for (const rda of rdas) {
    const key = rda.intervention_id != null ? rda.intervention_id : undefined
    if (!rdaByInt.has(key)) rdaByInt.set(key, [])
    rdaByInt.get(key)!.push(rda)
  }

  const RdaBadges = ({ list }: { list: PurchaseRequest[] }) => {
    const groups: Record<string, { count: number; color: string }> = {}
    for (const rda of list) {
      const info = rdaStatusInfo(rda)
      if (!groups[info.nome]) groups[info.nome] = { count: 0, color: info.color }
      groups[info.nome].count++
    }
    // I badge sono raggruppati per stato, quindi anche i resi si sommano: e'
    // la stessa informazione, riferita all'insieme delle offerte mostrate qui.
    const resi = list.reduce((tot, rda) => tot + ((rda as any).articoli_resi ?? 0), 0)
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
        {Object.entries(groups).map(([nome, { count, color }]) => (
          <Tag key={nome} color={color} style={{ fontSize: '11px', margin: 0 }}>
            {count} {nome}
          </Tag>
        ))}
        <TagArticoliResi quanti={resi} compatto />
      </div>
    )
  }

  const woLevelRdas = rdaByInt.get(undefined) ?? []

  return (
    <div style={{ padding: '8px 24px 12px', borderTop: `1px solid ${token.colorBorderSecondary}` }}>
      {/* Interventi */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: '11px', color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Interventi ({conclusi}/{ints.length} conclusi)
        </Text>
        {ints.length === 0
          ? <div style={{ color: token.colorTextTertiary, fontSize: '12px', marginTop: 4 }}>Nessun intervento</div>
          : (
            <div style={{ marginTop: 6 }}>
              {/* Header griglia */}
              <div style={{
                display: 'grid', gridTemplateColumns: INT_GRID, gap: '0 8px',
                paddingBottom: 4, marginBottom: 4,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}>
                {['#', 'Descrizione', 'Officina', 'Stato', ''].map((h, i) => (
                  <div key={i} style={{ fontSize: '11px', color: token.colorTextTertiary }}>{h}</div>
                ))}
              </div>
              {ints.map((it, i) => {
                const itRdas = rdaByInt.get(it.id) ?? []
                return (
                  <div key={it.id ?? i} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: INT_GRID, gap: '0 8px', alignItems: 'flex-start' }}>
                      <Text style={{ fontSize: '12px', color: token.colorTextTertiary, paddingTop: 2 }}>
                        {it.progressivo ?? i + 1}.
                      </Text>
                      <div>
                        <Text style={{ fontSize: '12px' }}>{it.descrizione_intervento}</Text>
                        {it.note_intervento && (
                          <div style={{ fontSize: '11px', color: token.colorTextSecondary, marginTop: 1 }}>
                            <span style={{ color: token.colorTextTertiary }}>nota: </span>{it.note_intervento}
                          </div>
                        )}
                        {(it.durata_stimata || it.ore_effettive) && (
                          <div style={{ fontSize: '11px', marginTop: 1 }}>
                            {it.durata_stimata && (
                              <span style={{ color: token.colorTextTertiary }}>est. {it.durata_stimata}h</span>
                            )}
                            {it.durata_stimata && it.ore_effettive && ' · '}
                            {it.ore_effettive && (
                              <span style={{ color: token.colorPrimary }}>eff. {it.ore_effettive}h</span>
                            )}
                          </div>
                        )}
                        {itRdas.length > 0 && <RdaBadges list={itRdas} />}
                      </div>
                      <div style={{ paddingTop: 2 }}>
                        {it.workshop_nome
                          ? <Tag style={{ fontSize: '11px' }}>{it.workshop_nome}</Tag>
                          : <Text style={{ fontSize: '12px', color: token.colorTextQuaternary }}>—</Text>
                        }
                      </div>
                      <div style={{ paddingTop: 2 }}>
                        <InterventionTag int={it} />
                      </div>
                      <div style={{ paddingTop: 4 }}>
                        {it.note_sospensione && (
                          <Tooltip title={it.note_sospensione}>
                            <WarningOutlined style={{ color: token.colorWarning, fontSize: 12 }} />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        }
      </div>

      {/* RDA generali (non legate a intervento) */}
      {woLevelRdas.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: '11px', color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            RDA generali ({woLevelRdas.length})
          </Text>
          <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {woLevelRdas.map(rda => {
              const info = rdaStatusInfo(rda)
              return (
                <Tag key={rda.id} color={info.color} style={{ fontSize: '11px' }}>
                  {rda.codice} — {info.nome}
                </Tag>
              )
            })}
          </div>
        </div>
      )}

      <Divider style={{ margin: '8px 0' }} />

      {/* Nota del giorno */}
      <div>
        <Text strong style={{ fontSize: '11px', color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Nota del giorno — {dayjs(selectedDate).format('DD/MM/YYYY')}
        </Text>
        <div style={{ marginTop: 6 }}>
          {isPast ? (
            noteText
              ? <Text style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{noteText}</Text>
              : <Text style={{ fontSize: '12px', color: token.colorTextTertiary }}>Nessuna nota per questo giorno</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              <TextArea
                rows={2}
                value={noteText}
                onChange={e => onNoteChange(e.target.value)}
                placeholder="Inserisci una nota per questo veicolo oggi..."
                style={{ fontSize: '13px' }}
              />
              <Button
                type="primary" size="small"
                loading={savingNote}
                disabled={!noteText.trim()}
                onClick={onNoteSave}
              >
                Salva nota
              </Button>
            </Space>
          )}
        </div>
      </div>
    </div>
  )
}

export default function BoardGiornalieroPage() {
  const { token } = theme.useToken()
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()
  const canViewADC = usePermission('courtesy_cars.view')

  // Split panel pixel-based: usa window.innerHeight per avere valori validi subito
  const DIVIDER_H = 8
  const MIN_PANEL = 120
  // 64px header Ant Design + 24px*2 margini Content
  const getContainerH = () => Math.max(300, window.innerHeight - 112)
  const [containerH, setContainerH] = useState(getContainerH)
  const [topPx, setTopPx] = useState(() => Math.floor(getContainerH() / 2))
  const containerRef = useRef<HTMLDivElement>(null)

  // Aggiorna quando la finestra viene ridimensionata
  useEffect(() => {
    const onResize = () => {
      const h = getContainerH()
      setContainerH(h)
      setTopPx(prev => Math.max(MIN_PANEL, Math.min(h - DIVIDER_H - MIN_PANEL, prev)))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleDividerDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startY = e.clientY
    const startTop = topPx

    const onMove = (mv: MouseEvent) => {
      const h = getContainerH()
      const newTop = startTop + (mv.clientY - startY)
      setTopPx(Math.max(MIN_PANEL, Math.min(h - DIVIDER_H - MIN_PANEL, newTop)))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [topPx])

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [notes, setNotes] = useState<Map<number, DailyNote>>(new Map())
  const [noteEdits, setNoteEdits] = useState<Map<number, string>>(new Map())
  const [savingNotes, setSavingNotes] = useState<Set<number>>(new Set())
  const [loadingNotes, setLoadingNotes] = useState(false)
  const [pastSnapshot, setPastSnapshot] = useState<BoardSnapshotItem[]>([])
  const [loadingSnapshot, setLoadingSnapshot] = useState(false)
  const [expandedRows, setExpandedRows] = useState<number[]>([])
  const [workshopMap, setWorkshopMap] = useState<Map<number, string[]>>(new Map())
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null)
  const [courtesyCarMap, setCourtesyCarMap] = useState<Map<number, { targa: string; marca?: string; modello?: string }>>(new Map())

  const isToday = selectedDate.isSame(dayjs(), 'day')
  const isPast = selectedDate.isBefore(dayjs(), 'day')
  const dateStr = selectedDate.format('YYYY-MM-DD')

  useEffect(() => { setTitle('Board Giornaliero') }, [setTitle])

  // WOs attivi: tutti gli stati escluso riconsegnata/annullata,
  // con data_entrata_veicolo <= oggi
  const todayStr = dayjs().format('YYYY-MM-DD')
  const { data: activeWOData, isLoading: loadingIL, refetch: refetchWO, dataUpdatedAt: woUpdatedAt } = useWorkOrders(
    1, 500, undefined, undefined, undefined, undefined, 'data_appuntamento', 'asc',
    undefined, undefined, undefined, undefined, todayStr
  )

  useEffect(() => {
    if (woUpdatedAt) setLastRefreshed(woUpdatedAt)
  }, [woUpdatedAt])

  // Gia' filtrate dal server con `entrata_a`: sono le auto in garage oggi.
  const todayWOs: WorkOrder[] = activeWOData?.items ?? []
  const filteredWOs = todayWOs

  // Date passate: lo snapshot resta una fotografia, con il criterio deciso dal
  // backend (gli stati attivi in QUELLA data). Qui non si filtra piu' nulla solo
  // perche' e' sparito il selettore di stato, che era l'unico filtro di pagina.
  const filteredSnapshot = pastSnapshot

  // Gli interventi di tutte le schede in una chiamata sola. La lista schede li
  // esclude con noload, e il dettaglio per scheda sarebbe una chiamata a testa:
  // /work-orders/interventi-attivi li porta in blocco, gia' ristretto alle auto
  // presenti dallo stesso criterio della lista. Il parametro `stati=in-officina`
  // toglie il restringimento per stato scheda, che vale solo per la vista
  // "Lavori in officina".
  //
  // ⚠️ Restituisce INTERVENTI: le schede che non ne hanno non compaiono qui.
  // E' voluto — sono quelle del gruppo "Da valutare", e la pagina le riconosce
  // proprio perche' la mappa non ha una riga per loro.
  const { data: interventiData } = useQuery({
    queryKey: ['board-interventi-attivi'],
    queryFn: async () => {
      const params = new URLSearchParams({ stati: ALIAS_STATI_IN_OFFICINA })
      const res = await axiosInstance.get<{ items: InterventoBoard[] }>(
        `/work-orders/interventi-attivi?${params.toString()}`
      )
      return res.data
    },
    enabled: isToday,
    retry: 1,
    staleTime: 0,
  })

  const interventiPerScheda = useMemo(() => {
    const mappa = new Map<number, InterventoBoard[]>()
    for (const iv of interventiData?.items ?? []) {
      const lista = mappa.get(iv.work_order_id) ?? []
      lista.push(iv)
      mappa.set(iv.work_order_id, lista)
    }
    return mappa
  }, [interventiData])

  // ── Stampa board ─────────────────────────────────────────────────────────────
  const [isPrinting, setIsPrinting] = useState(false)

  const handlePrint = async () => {
    setIsPrinting(true)
    try {
      // Fetch interventi per ogni scheda (sia live che snapshot storico)
      let righe: BoardPrintRow[]
      if (isPast) {
        const details = await Promise.all(
          filteredSnapshot.map(item =>
            axiosInstance.get(`/work-orders/${item.work_order_id}`)
              .then(res => ({ id: item.work_order_id, interventions: (res.data.interventions ?? []) as Intervention[] }))
              .catch(() => ({ id: item.work_order_id, interventions: [] as Intervention[] }))
          )
        )
        const intMap = new Map(details.map(d => [d.id, d.interventions]))
        righe = filteredSnapshot.map(item => ({
          numero_scheda: item.numero_scheda,
          vehicle_targa: item.vehicle_targa,
          vehicle_marca: item.vehicle_marca,
          vehicle_modello: item.vehicle_modello,
          customer_nome: item.customer_nome,
          stato: item.stato_al_momento,
          priorita: item.priorita,
          nota_testo: item.nota_testo,
          interventions: (intMap.get(item.work_order_id) ?? []).map(int => ({
            progressivo: int.progressivo,
            descrizione: int.descrizione_intervento,
            workshop_nome: int.workshop_nome ?? undefined,
            stato_nome: int.stato_intervento?.nome,
          })),
        }))
      } else {
        const details = await Promise.all(
          filteredWOs.map(wo =>
            axiosInstance.get(`/work-orders/${wo.id}`)
              .then(res => ({ id: wo.id, interventions: (res.data.interventions ?? []) as Intervention[] }))
              .catch(() => ({ id: wo.id, interventions: [] as Intervention[] }))
          )
        )
        const intMap = new Map(details.map(d => [d.id, d.interventions]))
        righe = filteredWOs.map(wo => ({
          numero_scheda: wo.numero_scheda ?? String(wo.id),
          vehicle_targa: wo.vehicle_targa,
          vehicle_marca: wo.vehicle_marca,
          vehicle_modello: wo.vehicle_modello,
          customer_nome: wo.customer_nome,
          stato: wo.stato ?? '',
          priorita: wo.priorita,
          nota_testo: notes.get(wo.id)?.testo,
          interventions: (intMap.get(wo.id) ?? []).map(int => ({
            progressivo: int.progressivo,
            descrizione: int.descrizione_intervento,
            workshop_nome: int.workshop_nome ?? undefined,
            stato_nome: int.stato_intervento?.nome,
          })),
        }))
      }
      printBoard(selectedDate.format('dddd D MMMM YYYY'), righe)
    } finally {
      setIsPrinting(false)
    }
  }

  // Note per la data (oggi): badge icons nella tabella today
  const loadNotes = useCallback(async (date: string) => {
    setLoadingNotes(true)
    try {
      const res = await axiosInstance.get(`/daily-notes/?date=${date}`)
      const map = new Map<number, DailyNote>()
      const editMap = new Map<number, string>()
      for (const n of res.data as DailyNote[]) {
        map.set(n.work_order_id, n)
        editMap.set(n.work_order_id, n.testo)
      }
      setNotes(map)
      setNoteEdits(editMap)
    } catch {
      // silenzioso
    } finally {
      setLoadingNotes(false)
    }
  }, [])

  // Snapshot storico: WO attivi in quella data + note overlay
  const loadSnapshot = useCallback(async (date: string) => {
    setLoadingSnapshot(true)
    try {
      const res = await axiosInstance.get(`/daily-notes/snapshot?date=${date}`)
      const items: BoardSnapshotItem[] = res.data
      setPastSnapshot(items)
      const editMap = new Map<number, string>()
      for (const item of items) {
        if (item.nota_testo) editMap.set(item.work_order_id, item.nota_testo)
      }
      setNoteEdits(editMap)
    } catch {
      // silenzioso
    } finally {
      setLoadingSnapshot(false)
    }
  }, [])


  useEffect(() => {
    setExpandedRows([])
    if (isPast) {
      loadSnapshot(dateStr)
    } else {
      loadNotes(dateStr)
    }
    axiosInstance.get<Record<string, { targa: string; marca?: string; modello?: string }>>(`/daily-notes/courtesy-car-assignments?date=${dateStr}`)
      .then(res => {
        const map = new Map<number, { targa: string; marca?: string; modello?: string }>()
        for (const [k, v] of Object.entries(res.data)) map.set(Number(k), v)
        setCourtesyCarMap(map)
      })
      .catch(() => setCourtesyCarMap(new Map()))
  }, [dateStr, isPast, loadSnapshot, loadNotes])

  // Carica officine per la vista odierna (una sola query batch dopo che todayWOs è pronto)
  useEffect(() => {
    if (isPast || todayWOs.length === 0) { setWorkshopMap(new Map()); return }
    const params = todayWOs.map(wo => `wo_ids=${wo.id}`).join('&')
    axiosInstance.get<Record<string, string[]>>(`/daily-notes/intervention-workshops?${params}`)
      .then(res => {
        const map = new Map<number, string[]>()
        for (const [k, v] of Object.entries(res.data)) map.set(Number(k), v)
        setWorkshopMap(map)
      })
      .catch(() => {})
  }, [isPast, todayWOs.length])

  const saveNote = async (woId: number) => {
    const testo = noteEdits.get(woId)?.trim()
    if (!testo) return
    setSavingNotes(prev => new Set(prev).add(woId))
    try {
      const res = await axiosInstance.put(`/daily-notes/work-order/${woId}/?date=${dateStr}`, { testo })
      setNotes(prev => new Map(prev).set(woId, res.data))
      message.success('Nota salvata')
    } catch {
      message.error('Errore nel salvataggio nota')
    } finally {
      setSavingNotes(prev => { const s = new Set(prev); s.delete(woId); return s })
    }
  }

  const handleRefresh = useCallback(() => {
    if (isPast) {
      loadSnapshot(dateStr)
    } else {
      refetchWO()
      loadNotes(dateStr)
    }
    setLastRefreshed(Date.now())
  }, [isPast, dateStr, loadSnapshot, loadNotes, refetchWO])

  useEffect(() => {
    const id = setInterval(handleRefresh, 60_000)
    return () => clearInterval(id)
  }, [handleRefresh])

  // Tabella per oggi: WOs attivi
  const pastColumns: ColumnsType<BoardSnapshotItem> = [
    {
      title: 'Targa',
      key: 'targa',
      width: 90,
      render: (_, item) => (
        <Text strong style={{ fontFamily: 'monospace', fontSize: '13px' }}>{item.vehicle_targa ?? '—'}</Text>
      ),
    },
    {
      title: 'Veicolo',
      key: 'veicolo',
      width: 160,
      render: (_, item) => (
        <div style={{ lineHeight: 1.3 }}>
          <div style={{ fontSize: '13px', fontWeight: 500 }}>
            {[item.vehicle_marca, item.vehicle_modello].filter(Boolean).join(' ') || '—'}
          </div>
          {item.vehicle_anno && <div style={{ fontSize: '11px', color: token.colorTextTertiary }}>{item.vehicle_anno}</div>}
        </div>
      ),
    },
    {
      title: 'Cliente',
      key: 'cliente',
      width: 160,
      render: (_, item) => (
        <div style={{ lineHeight: 1.3 }}>
          <div style={{ fontSize: '13px' }}>{item.customer_nome ?? '—'}</div>
          {item.customer_telefono && (
            <div style={{ fontSize: '11px', color: token.colorTextTertiary }}>{item.customer_telefono}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Stato',
      key: 'stato',
      width: 110,
      render: (_, item) => (
        <Tag color={item.stato_al_momento === 'sospesa' ? 'orange' : 'blue'} style={{ fontSize: '11px' }}>
          {item.stato_al_momento}
        </Tag>
      ),
    },
    {
      title: 'Appunt.',
      key: 'appuntamento',
      width: 80,
      render: (_, item) => item.data_appuntamento
        ? <Text style={{ fontSize: '12px' }}>{dayjs(item.data_appuntamento).format('DD/MM')}</Text>
        : '—',
    },
    {
      title: 'Entrata',
      key: 'entrata_veicolo',
      width: 80,
      render: (_, item) => item.data_entrata_veicolo
        ? <Text style={{ fontSize: '12px' }}>{dayjs(item.data_entrata_veicolo).format('DD/MM')}</Text>
        : <Text style={{ fontSize: '12px', color: 'var(--ant-color-text-quaternary)' }}>—</Text>,
    },
    {
      title: 'Scad.',
      key: 'scadenza',
      width: 80,
      render: (_, item) => {
        if (!item.data_fine_prevista) return '—'
        const isScaduta = dayjs(item.data_fine_prevista).isBefore(dayjs(), 'day')
        return (
          <Text style={{ fontSize: '12px', color: isScaduta ? token.colorError : undefined }}>
            {dayjs(item.data_fine_prevista).format('DD/MM')}
          </Text>
        )
      },
    },
    {
      title: 'Officine',
      key: 'officine',
      width: 150,
      render: (_, item) => <WorkshopTags nomi={item.workshop_nomi} />,
    },
    {
      title: 'ADC',
      key: 'adc',
      width: 110,
      render: (_, item) => {
        const adc = courtesyCarMap.get(item.work_order_id)
        if (!adc) return <Text style={{ fontSize: '12px', color: token.colorTextQuaternary }}>—</Text>
        const mm = [adc.marca, adc.modello].filter(Boolean).join(' ')
        return (
          <div>
            <Text strong style={{ fontFamily: 'monospace', fontSize: '12px' }}>{adc.targa}</Text>
            {mm && <div><Text style={{ fontSize: '11px', fontWeight: 300, color: token.colorTextSecondary }}>{mm}</Text></div>}
          </div>
        )
      },
    },
    {
      title: '',
      key: 'azioni',
      width: 42,
      align: 'center',
      render: (_, item) => (
        <Tooltip title="Apri scheda lavoro">
          <Button
            type="text" size="small" icon={<ArrowRightOutlined />}
            onClick={e => { e.stopPropagation(); navigate(`/schede-lavoro/${item.work_order_id}`) }}
          />
        </Tooltip>
      ),
    },
  ]

  const pastExpandable: ExpandableConfig<BoardSnapshotItem> = {
    expandedRowKeys: expandedRows,
    onExpandedRowsChange: keys => setExpandedRows(keys as number[]),
    showExpandColumn: false,
    expandIcon: () => null,
    expandedRowRender: (item) => (
      <WOExpandedRow
        wo={{ id: item.work_order_id } as WorkOrder}
        selectedDate={dateStr}
        noteText={noteEdits.get(item.work_order_id) ?? ''}
        onNoteChange={() => {}}
        onNoteSave={() => {}}
        savingNote={false}
        isPast={true}
      />
    ),
    rowExpandable: () => true,
  }

  const isLoading = isPast ? loadingSnapshot : loadingIL

  const effectiveTop = topPx ?? 300
  const effectiveBottom = containerH > 0 ? containerH - DIVIDER_H - effectiveTop : 300

  return (
    <div
      ref={containerRef}
      style={{
        height: containerH,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* ── Metà superiore: Board Giornaliero ── */}
      <div style={{ height: canViewADC ? effectiveTop : '100%', overflow: 'auto', flexShrink: 0 }}>
        <div style={{ padding: '16px 20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Typography.Title level={4} style={{ margin: 0 }}>Board Giornaliero</Typography.Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
              <Button icon={<LeftOutlined />} size="small" onClick={() => setSelectedDate(d => d.subtract(1, 'day'))} />
              <DatePicker
                value={selectedDate}
                onChange={d => d && setSelectedDate(d)}
                format="DD/MM/YYYY"
                allowClear={false}
                size="small"
                style={{ width: 120 }}
              />
              <Button icon={<RightOutlined />} size="small" disabled={isToday} onClick={() => setSelectedDate(d => d.add(1, 'day'))} />
              <AutoRefreshBadge updatedAt={lastRefreshed} />
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRefresh} loading={isLoading || loadingNotes} style={{ marginLeft: 4 }}>
                Aggiorna
              </Button>
              <Button size="small" icon={<PrinterOutlined />} onClick={handlePrint} loading={isPrinting}>
                Stampa
              </Button>
            </div>
          </div>

          {/* Badge data */}
          <div style={{ marginBottom: 12 }}>
            {isToday
              ? <Tag color="blue">Oggi — {selectedDate.format('dddd DD MMMM YYYY')}</Tag>
              : <Tag color="default">{selectedDate.format('dddd DD MMMM YYYY')}</Tag>
            }
            {isToday && !isLoading && (
              <Text style={{ fontSize: '12px', color: token.colorTextSecondary, marginLeft: 8 }}>
                {filteredWOs.length} veicoli in officina
              </Text>
            )}
            {isPast && !loadingSnapshot && (
              <Text style={{ fontSize: '12px', color: token.colorTextSecondary, marginLeft: 8 }}>
                {filteredSnapshot.length} veicoli
                {filteredSnapshot.filter(x => x.nota_testo).length > 0 && (
                  <> · {filteredSnapshot.filter(x => x.nota_testo).length} con nota</>
                )}
              </Text>
            )}
          </div>

          {/* Oggi: cinque gruppi per urgenza, non un elenco piatto. Una lista di
              settanta righe dice cosa c'e' in officina, non di cosa parlare. */}
          {isToday && (
            isLoading
              ? <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
              : (
                <BoardGruppi
                  workOrders={filteredWOs}
                  interventiPerScheda={interventiPerScheda}
                  officinePerScheda={workshopMap}
                  autoCortesiaPerScheda={courtesyCarMap}
                  noteEdits={noteEdits}
                  savingNotes={savingNotes}
                  onNotaChange={(woId, testo) => setNoteEdits(prev => new Map(prev).set(woId, testo))}
                  onNotaSave={saveNote}
                  onApriScheda={woId => navigate(`/schede-lavoro/${woId}`)}
                />
              )
          )}

          {/* Tabella date passate: snapshot storico */}
          {isPast && (
            loadingSnapshot
              ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
              : pastSnapshot.length === 0
                ? <Empty description={`Nessun veicolo in lavorazione il ${selectedDate.format('DD/MM/YYYY')}`} style={{ marginTop: 40 }} />
                : (
                  <Table<BoardSnapshotItem>
                    rowKey="work_order_id"
                    columns={pastColumns}
                    dataSource={filteredSnapshot}
                    expandable={pastExpandable}
                    pagination={false}
                    size="small"
                    scroll={{ x: 1002 }}
                    onRow={(item) => ({
                      style: { cursor: 'pointer' },
                      onClick: () => setExpandedRows(prev =>
                        prev.includes(item.work_order_id)
                          ? prev.filter(k => k !== item.work_order_id)
                          : [...prev, item.work_order_id]
                      ),
                    })}
                  />
                )
          )}
        </div>
      </div>

      {canViewADC && (
        <>
          {/* ── Divisore trascinabile ── */}
          <div
            onMouseDown={handleDividerDown}
            style={{
              height: 8,
              flexShrink: 0,
              cursor: 'row-resize',
              background: token.colorBorderSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = token.colorBorder }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = token.colorBorderSecondary }}
          >
            <div style={{ width: 48, height: 3, borderRadius: 2, background: token.colorBorder, opacity: 0.6 }} />
          </div>

          {/* ── Metà inferiore: Pianificazione Auto di Cortesia ── */}
          <div style={{ height: effectiveBottom, overflow: 'auto', flexShrink: 0 }}>
            {/* Intestazione sezione */}
            <div style={{
              padding: '6px 20px',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              background: token.colorFillAlter,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <CarOutlined style={{ color: token.colorPrimary, fontSize: 13 }} />
              <Typography.Text strong style={{ fontSize: 13 }}>Auto di Cortesia</Typography.Text>
            </div>
            <CourtesyCarPlanningPanel />
          </div>
        </>
      )}
    </div>
  )
}
